import { ENTITY_TYPES } from '@sales-center/common'

import { selectAllCustomFieldsSettings } from '@user/store/customFieldSettings'
import { selectAllEntityFormSettings } from '@user/store/entityFormSettings'
import type {
  ICustomFieldFormSetting,
  IEntityFormSetting,
} from '@user/store/entityFormSettings/typings'
import { StoreData } from '@user/store/reducers/typings'

export type BuiltinFieldSetting = { source: 'builtin' } & IEntityFormSetting
export type CustomFieldSetting = { source: 'custom' } & ICustomFieldFormSetting

/**
 * A field setting from either source, for use on this settings page.
 *
 * Built-in and custom fields each keep every property their own type carries;
 * nothing is flattened or renamed. Branch on the `source` discriminator (or
 * the type guards below) rather than testing for the presence of a property.
 */
export type UnifiedFieldSetting = BuiltinFieldSetting | CustomFieldSetting

export const isBuiltinFieldSetting = (
  item: UnifiedFieldSetting,
): item is BuiltinFieldSetting => item.source === 'builtin'

export const isCustomFieldSetting = (
  item: UnifiedFieldSetting,
): item is CustomFieldSetting => item.source === 'custom'

/**
 * Selects the built-in and custom field settings as one tagged list.
 *
 * A read-only composition over the `entityFormSettings` and
 * `customFieldSettings` slices. It reuses each slice's own selectors and
 * changes neither, and it lives with the settings page rather than in the
 * store because the two slices remain deliberately separate — they call
 * different endpoints and have different write semantics.
 *
 * Built-in settings are filtered by `entityType`, since that slice can hold
 * either entity's rows depending on what was fetched last. Custom settings
 * are not filtered: `ICustomFieldFormSetting` carries no entity type, and the
 * fetch thunk replaces the whole collection per entity, so the slice only ever
 * holds the current entity's fields.
 *
 * @param entityType The entity whose settings to select.
 */
export const selectUnifiedFieldSettings =
  (entityType: ENTITY_TYPES) =>
  (state: StoreData): UnifiedFieldSetting[] => {
    const builtin: UnifiedFieldSetting[] = selectAllEntityFormSettings(state)
      .filter((setting) => setting.entityType === entityType)
      .map((setting) => ({ source: 'builtin', ...setting }))

    const custom: UnifiedFieldSetting[] = selectAllCustomFieldsSettings(
      state,
    ).map((setting) => ({ source: 'custom', ...setting }))

    return [...builtin, ...custom]
  }
